import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import type { RootState, AppDispatch } from '../../redux/store';
import { fetchCompetitorsRequest } from '../../redux/slices/competitorsSlice';
import PageHeader from '../common/PageHeader';
import LoadingState from '../common/LoadingState';
import ErrorState from '../common/ErrorState';
import BroadCompetitorView from './BroadCompetitorView';
import DirectCompetitorView from './DirectCompetitorView';

const Competitors: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { restaurantId } = useParams<{ restaurantId: string }>();
  const { broadCompetitor, directCompetitors, loading, error } = useSelector(
    (state: RootState) => state.competitors
  );
  const [activeTab, setActiveTab] = useState<'direct' | 'broad'>('direct');

  useEffect(() => {
    if (restaurantId) {
      dispatch(fetchCompetitorsRequest(restaurantId));
    }
  }, [dispatch, restaurantId]);

  if (loading) {
    return <LoadingState page="competitor analysis" />;
  }

  if (error) {
    return (
      <ErrorState 
        message="Failed to load competitor analysis" 
        error={error}
        onRetry={() => restaurantId && dispatch(fetchCompetitorsRequest(restaurantId))}
      />
    );
  }

  return (
    <div className="min-h-screen">
      <PageHeader title="Competition Analysis for Delaney" />

      {/* Tabs */}
      <div className="flex gap-2 mt-6">
        <button
          onClick={() => setActiveTab('direct')}
          className={`px-6 py-3 cursor-pointer rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
            activeTab === 'direct'
              ? 'bg-[#EE4423] text-white shadow-lg'
              : 'bg-[#212121] text-gray-300 hover:bg-[#333333] border border-[#4a4a4a]'
          }`}
        >
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
          </svg>
          Direct Competition
          {/* {directCompetitors.length > 0 && (
            <span className="px-2 py-0.5 bg-white/20 rounded text-xs">
              {directCompetitors.length} competitors
            </span>
          )} */}
        </button>

        <button
          onClick={() => setActiveTab('broad')}
          className={`px-6 py-3 cursor-pointer rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
            activeTab === 'broad'
              ? 'bg-[#EE4423] text-white shadow-lg'
              : 'bg-[#212121] text-gray-300 hover:bg-[#333333] border border-[#4a4a4a]'
          }`}
        >
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M3 5a2 2 0 012-2h10a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V5zm11 1H6v8h8V6z"
              clipRule="evenodd"
            />
          </svg>
          Broad Competition
        </button>
      </div>

      {/* Content based on active tab */}
      <div className="mt-6">
        {activeTab === 'direct' ? (
          <DirectCompetitorView competitors={directCompetitors} />
        ) : (
          <BroadCompetitorView data={broadCompetitor} />
        )}
      </div>
    </div>
  );
};

export default Competitors;
